import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface Invite {
  id: string;
  code: string;
  type: "platform" | "team" | "both";
  team_id: string | null;
  email: string | null;
  invited_by: string;
  team_role: string;
  max_uses: number | null;
  use_count: number;
  expires_at: string | null;
  created_at: string;
  team_name?: string;
}

export interface CreateInviteRequest {
  type?: "platform" | "team" | "both";
  team_id?: string;
  email?: string;
  team_role?: string;
  max_uses?: number;
  expires_in_hours?: number;
}

export interface ValidateResponse {
  valid: boolean;
  type?: string;
  team_name?: string;
  team_role?: string;
  email?: string;
}

const inviteKeys = {
  all: ["invites"] as const,
  list: () => ["invites", "list"] as const,
};

export function useInvitesQuery() {
  return useQuery({
    queryKey: inviteKeys.list(),
    queryFn: () => apiFetch<Invite[]>("/invites"),
  });
}

export function useCreateInviteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInviteRequest) =>
      apiFetch<Invite>("/invites", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteKeys.all });
    },
  });
}

export function useRevokeInviteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/invites/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteKeys.all });
    },
  });
}

/** Validate an invite code — public, no auth needed. */
export async function validateInviteCode(code: string): Promise<ValidateResponse> {
  return apiFetch<ValidateResponse>(`/invites/validate/${encodeURIComponent(code)}`);
}
